import React from 'react';
import {
    Box, ToggleButton, ToggleButtonGroup
} from '@mui/material';

import type { TenantRoute, TenantRoutePoint } from '../../../types/TenantRouteType';
import LocationPicker from './LocationPicker';
import type { LocationType } from './LocationPicker';
import EditableRouteMap from './EditableRouteMap';
import type { EditableRouteMapHandle } from './EditableRouteMap';
import EditableCircleMap from './EditableCircleMap';
import type { ColoredRoute } from './RoutePolygons';

export type AreaShape = 'polygon' | 'circle';

type PropsType = {
    shape: AreaShape,
    onShapeChange: (shape: AreaShape) => void,
    /** Show the Polygon | Cirkel toggle over the map */
    showToggle?: boolean,
    height?: number | string,
    // Polygon
    routes: TenantRoute[],
    selectedId?: number | null,
    fitToId?: number | null,
    onSelect?: (id: number) => void,
    onPointsChange: (id: number, points: TenantRoutePoint[]) => void,
    // Circle
    circleCenter: TenantRoutePoint | null,
    circleRadiusM: number,
    onCircleChange: (center: TenantRoutePoint, radiusM: number) => void,
    /** Shown behind the circle (non-editable) */
    otherRoutes?: ColoredRoute[]
};

/** Polygon or circle editor with the shape toggle overlaid top-left, as in the watchlist form. */
const ShapeAreaEditor = React.forwardRef<EditableRouteMapHandle, PropsType>((props, ref) => {
    const {
        shape,
        onShapeChange,
        showToggle = true,
        height,
        routes,
        selectedId,
        fitToId,
        onSelect,
        onPointsChange,
        circleCenter,
        circleRadiusM,
        onCircleChange,
        otherRoutes
    } = props;
    const isCircle = shape === 'circle';

    return (
        <>
            {isCircle && (
                <Box sx={{ mb: 1 }}>
                    <LocationPicker
                        label="Sök plats (valfritt)"
                        onChange={(l: LocationType | null) => {
                            if (l) onCircleChange({ lat: l.lat, lng: l.lng }, circleRadiusM);
                        }}
                    />
                </Box>
            )}
            <Box sx={{ position: 'relative' }}>
                {isCircle ? (
                    <EditableCircleMap
                        center={circleCenter}
                        radiusM={circleRadiusM}
                        onChange={onCircleChange}
                        otherRoutes={otherRoutes}
                        height={height}
                    />
                ) : (
                    <EditableRouteMap
                        ref={ref}
                        routes={routes}
                        selectedId={selectedId}
                        fitToId={fitToId}
                        onSelect={onSelect}
                        onPointsChange={onPointsChange}
                        height={height}
                    />
                )}
                {showToggle && (
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={shape}
                        onChange={(_e, next: AreaShape | null) => {
                            if (next) onShapeChange(next);
                        }}
                        aria-label="Form på området"
                        sx={{
                            position: 'absolute', top: 10, left: 10, bgcolor: 'background.paper'
                        }}
                    >
                        <ToggleButton value="polygon">Polygon</ToggleButton>
                        <ToggleButton value="circle">Cirkel</ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>
        </>
    );
});

export default ShapeAreaEditor;
